import { META_BASE_PATH, META_CLIENT } from './APIResource.decorator';

export const META_METHOD_PATH = Symbol('method_path');
const META_HTTP_METHOD = Symbol('http_method');
const META_METHOD_OPTIONS = Symbol('method_options');
const META_REQUEST_TYPE = Symbol('request_type');
const META_RESPONSE_TYPE = Symbol('response_type');

/**
 * Ambient variable written by the decorated method wrapper immediately before
 * the original method body executes. createFetch / createCommit read this
 * synchronously — no Error.stack parsing needed, works in all browsers
 * including Safari with JIT optimisation enabled.
 */
let _currentMethodName = '';

export function getCurrentDecoratedMethodName(): string {
  return _currentMethodName;
}

export interface MethodOptions {
  cacheTime?: number; // Deprecated: use gcTime instead
  gcTime?: number; // Cache garbage collection time (React Query v5)
  staleTime?: number;
  retry?: number;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
}

/**
 * Type information stored in decorator metadata
 */
export interface TypeMetadata<TRequest = unknown, TResponse = unknown> {
  requestType?: new () => TRequest;
  responseType?: new () => TResponse;
}

/**
 * Creates a typed method decorator with explicit request and response types
 * The type parameters are used for compile-time type checking at call sites
 * @template TRequest - The request payload/query type
 * @template TResponse - The response data type
 */
export function createMethodDecorator(httpMethod: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return function <TRequest = void, TResponse = unknown>(
    path: string,
    options?: MethodOptions,
  ): MethodDecorator {
    return (
      target: object,
      propertyKey: string | symbol,
      descriptor: PropertyDescriptor,
    ): PropertyDescriptor => {
      Reflect.defineMetadata(META_METHOD_PATH, path, target, propertyKey);
      Reflect.defineMetadata(META_HTTP_METHOD, httpMethod, target, propertyKey);
      if (options) {
        Reflect.defineMetadata(META_METHOD_OPTIONS, options, target, propertyKey);
      }
      // Store type metadata for runtime access
      Reflect.defineMetadata(META_REQUEST_TYPE, 'TRequest', target, propertyKey);
      Reflect.defineMetadata(META_RESPONSE_TYPE, 'TResponse', target, propertyKey);

      // Wrap the method body to set the ambient name before createFetch/createCommit runs.
      // This is read synchronously and cleared in the finally block — Safari-safe.
      const original = descriptor.value as (...args: unknown[]) => unknown;
      descriptor.value = function (this: object, ...args: unknown[]) {
        _currentMethodName = String(propertyKey);
        try {
          return original.apply(this, args);
        } finally {
          _currentMethodName = '';
        }
      };
      return descriptor;
    };
  };
}

/**
 * GET decorator with type parameters
 * @template TRequest - Query parameters type
 * @template TResponse - Response data type
 * @example
 * ```typescript
 * @GET<ListTeamsQuery, ListTeamsResponse>("/")
 * getTeams() { return createFetch(this); }
 * ```
 */
export const GET = createMethodDecorator('GET');

/**
 * POST decorator with type parameters
 * @template TRequest - Request payload type
 * @template TResponse - Response data type
 * @example
 * ```typescript
 * @POST<CreateTeamRequest, Team>("/")
 * createTeam() { return createCommit(this); }
 * ```
 */
export const POST = createMethodDecorator('POST');

/**
 * PUT decorator with type parameters
 * @template TRequest - Request payload type
 * @template TResponse - Response data type
 * @example
 * ```typescript
 * @PUT<UpdateTeamRequest, Team>("/:id")
 * updateTeam() { return createCommit(this); }
 * ```
 */
export const PUT = createMethodDecorator('PUT');

/**
 * PATCH decorator with type parameters
 * @template TRequest - Request payload type (partial update)
 * @template TResponse - Response data type
 * @example
 * ```typescript
 * @PATCH<ModifyTeamRequest, Team>("/:id")
 * modifyTeam() { return createCommit(this); }
 * ```
 */
export const PATCH = createMethodDecorator('PATCH');

/**
 * DELETE decorator with type parameters
 * @template TRequest - Request payload type (usually just id)
 * @template TResponse - Response data type (often void)
 * @example
 * ```typescript
 * @DELETE<{ id: string }, void>("/:id")
 * deleteTeam() { return createCommit(this); }
 * ```
 */
export const DELETE = createMethodDecorator('DELETE');

export function resolveMethodMetadata(instance: object, methodName: string) {
  const proto = Object.getPrototypeOf(instance);

  const clientClass = Reflect.getMetadata(META_CLIENT, proto);
  const basePath = Reflect.getMetadata(META_BASE_PATH, proto) || '';
  const methodPath = Reflect.getMetadata(META_METHOD_PATH, proto, methodName) || '';
  const httpMethod = Reflect.getMetadata(META_HTTP_METHOD, proto, methodName) || 'GET';
  const methodOptions = Reflect.getMetadata(META_METHOD_OPTIONS, proto, methodName) || {};

  return {
    clientClass,
    httpMethod,
    fullPath: `${basePath}${methodPath}`,
    methodOptions,
  };
}
